// Sealbound Bulwark (replaces `warden`) — elite: Vault Sentinel. Render-only art (ENEMY-REFORGE, P1 §2 #10, §4).
// Geometric and orderly: square-cut dark-iron armour, a broad mechanical shoulder on the weapon side (-X) and a big
// ivory rectangular shield carried on the +X side facing forward. Upright armoured march.
// Wind-up: the shield plants forward and down (a far pivot above-front of the body turns the rotation into a thrust).
import { ArtBuilder, ArtGeo, at, EnemyArt, limb, PAL, plate } from './kit';
import { armPhase, drum, legPhase } from './parts_heavy';

const HIP_Y = 1.2, LEG_SWING = 0.36, ARM_SWING = 0.28;
const SHIELD_X = 0.74;
/** far pivot: a small pitch about it is (almost) a forward-and-down translation of the shield */
const SHIELD_PIVOT: [number, number, number] = [SHIELD_X, 6.0, 4.8];

function build(elite: boolean): ArtGeo {
  const b = new ArtBuilder();

  // ---- legs: square armoured columns
  for (const s of [-1, 1]) {
    const leg = { pivot: [s * 0.34, HIP_Y, 0] as [number, number, number], swing: LEG_SWING, sphase: legPhase(s) };
    b.add(at(plate(0.34, 0.58, 0.4, 0.05), s * 0.35, 0.96, 0), { color: PAL.iron, ...leg });
    b.add(at(plate(0.3, 0.42, 0.08, 0.03), s * 0.35, 1.0, 0.21), { color: PAL.ivoryShade, ...leg });
    b.add(at(plate(0.24, 0.16, 0.14, 0.04), s * 0.35, 0.66, 0.2), { color: PAL.copper, ...leg });
    b.add(at(plate(0.3, 0.5, 0.36, 0.05), s * 0.35, 0.4, 0), { color: PAL.iron, ...leg });
    b.add(at(plate(0.28, 0.4, 0.08, 0.03), s * 0.35, 0.42, 0.19), { color: PAL.ivory, ...leg });
    b.add(at(plate(0.38, 0.16, 0.6, 0.05), s * 0.35, 0.08, 0.07), { color: PAL.ironLight, ...leg });
  }

  // ---- waist, torso block, chest plate
  b.add(at(plate(0.82, 0.3, 0.5, 0.05), 0, 1.26, 0), { color: PAL.iron });
  for (const s of [-1, 1]) b.add(at(plate(0.3, 0.36, 0.06, 0.02), s * 0.2, 1.09, 0.27, -0.12), { color: PAL.ivoryShade });
  b.add(at(plate(0.98, 0.86, 0.6, 0.06), 0, 1.8, 0), { color: PAL.iron });
  b.add(at(plate(0.76, 0.56, 0.08, 0.03), 0, 1.9, 0.31), { color: PAL.ivory });
  b.add(at(plate(0.06, 0.5, 0.04, 0.01), 0, 1.9, 0.36), { color: PAL.copper });
  b.add(at(plate(0.88, 0.08, 0.62, 0.02), 0, 1.42, 0), { color: PAL.copper });
  b.add(at(plate(0.52, 0.16, 0.46, 0.04), 0, 2.27, 0.02), { color: PAL.ironLight });

  // ---- helm: square ivory helm, iron visor, teal slit, copper ridge
  b.add(at(plate(0.4, 0.4, 0.44, 0.08), 0, 2.47, 0.04), { color: PAL.ivory });
  b.add(at(plate(0.36, 0.12, 0.06, 0.02), 0, 2.46, 0.27), { color: PAL.iron });
  b.add(at(plate(0.26, 0.035, 0.03, 0.005, 1), 0, 2.47, 0.305), { color: PAL.teal, glow: true });
  b.add(at(plate(0.06, elite ? 0.24 : 0.12, 0.44, 0.02), 0, elite ? 2.75 : 2.7, 0.02), { color: PAL.copper });

  // ---- weapon-side broad mechanical shoulder (-X)
  b.add(at(plate(0.56, 0.32, 0.64, 0.06), -0.68, 2.17, 0), { color: PAL.iron });
  b.add(at(plate(0.52, 0.1, 0.58, 0.03), -0.7, 2.37, 0, 0, 0, 0.1), { color: PAL.ivory });
  b.add(at(plate(0.58, 0.06, 0.66, 0.02), -0.68, 2.0, 0), { color: PAL.copper });
  b.add(drum(0.08, 0.08, 0.3, -0.5, 2.2, -0.36, Math.PI / 2, 0, 0, 8), { color: PAL.copperDark });
  // shield-side shoulder (+X), smaller
  b.add(at(plate(0.4, 0.28, 0.6, 0.05), 0.63, 2.15, 0.02), { color: PAL.ivoryShade });

  if (elite) {
    // Vault Sentinel: extra shoulder-mounted armour — stacked plates and an upright shoulder ward on the weapon side
    // stacked lames widen the weapon shoulder, a second set on the shield shoulder, and a square collar ward behind the helm
    b.add(at(plate(0.7, 0.22, 0.78, 0.05), -0.74, 2.47, 0, 0, 0, 0.18), { color: PAL.ivory });
    b.add(at(plate(0.12, 0.56, 0.76, 0.04), -1.07, 2.16, 0, 0, 0, 0.12), { color: PAL.ivoryShade });
    b.add(at(plate(0.14, 0.07, 0.78, 0.02, 1), -1.11, 1.9, 0, 0, 0, 0.12), { color: PAL.copper });
    b.add(at(plate(0.48, 0.1, 0.56, 0.03), 0.66, 2.33, 0, 0, 0, -0.25), { color: PAL.ivory });
    b.add(at(plate(0.48, 0.06, 0.56, 0.02, 1), 0.66, 2.04, 0), { color: PAL.copper });
    b.add(at(plate(0.86, 0.5, 0.1, 0.03), 0, 2.5, -0.3, 0.12), { color: PAL.iron });
    b.add(at(plate(0.9, 0.06, 0.12, 0.01, 1), 0, 2.76, -0.33, 0.12), { color: PAL.copper });
  }

  // ---- weapon arm (-X): square gauntlet and a short flanged warhammer
  const arm = { pivot: [-0.66, 2.1, 0] as [number, number, number], swing: ARM_SWING, sphase: armPhase(-1), raise: 0.25 };
  b.add(at(plate(0.24, 0.56, 0.26, 0.05), -0.72, 1.76, 0), { color: PAL.iron, ...arm });
  b.add(at(plate(0.3, 0.5, 0.32, 0.05), -0.74, 1.27, 0.04), { color: PAL.ironLight, ...arm });
  b.add(at(plate(0.32, 0.07, 0.34, 0.02), -0.74, 1.48, 0.04), { color: PAL.copper, ...arm });
  b.add(limb([-0.74, 1.1, 0.06], [-0.74, 0.62, 0.42], 0.045, 0.045, 6), { color: PAL.iron, ...arm });
  b.add(at(plate(0.24, 0.24, 0.46, 0.04), -0.74, 0.6, 0.46, 0.93), { color: PAL.ironLight, ...arm });
  b.add(at(plate(0.26, 0.26, 0.06, 0.01), -0.74, 0.6, 0.46, 0.93), { color: PAL.copper, ...arm });

  // ---- shield arm (+X): upper arm swings from the shoulder; forearm + shield ride the far pivot
  // (arm + shield move as one rigid unit; the upper arm runs up inside the pauldron so the thrust never shows a gap)
  const sh = { pivot: SHIELD_PIVOT, swing: 0.012, sphase: armPhase(1), raise: -0.06 };
  b.add(at(plate(0.22, 0.72, 0.24, 0.05), 0.68, 1.9, 0.06, -0.3), { color: PAL.iron, ...sh });
  b.add(at(plate(0.24, 0.4, 0.6, 0.05), 0.7, 1.5, 0.3), { color: PAL.iron, ...sh });

  const W = elite ? 1.18 : 1.0, H = elite ? 2.3 : 1.66, cy = elite ? 1.33 : 1.33, z = 0.66;
  const yaw = -0.08;
  const put = (w: number, h: number, d: number, x: number, y: number, zz: number, col: number, glow = false, r = 0.02) =>
    b.add(at(plate(w, h, d, r, d <= 0.06 ? 1 : 2), SHIELD_X + x, y, z + zz, 0, yaw, 0), { color: col, glow, ...sh });
  put(W + 0.08, H + 0.08, 0.08, 0, cy, -0.05, PAL.iron, false, 0.03);   // iron backing / rim
  put(W, H, 0.12, 0, cy, 0, PAL.ivory, false, 0.04);                    // ivory face
  put(W + 0.02, 0.08, 0.04, 0, cy - H / 2 + 0.16, 0.07, PAL.copper);    // copper bands
  put(W + 0.02, 0.08, 0.04, 0, cy + H / 2 - 0.16, 0.07, PAL.copper);
  put(0.08, H - 0.1, 0.04, 0, cy, 0.07, PAL.copper);
  b.add(at(drum(0.17, 0.2, 0.08, 0, 0, 0, Math.PI / 2, 0, 0, 10), SHIELD_X, cy + 0.1, z + 0.1, 0, yaw, 0), { color: PAL.copperDark, ...sh });
  b.add(at(plate(0.09, 0.09, 0.04, 0.005, 1), SHIELD_X, cy + 0.1, z + 0.15, 0, yaw, Math.PI / 4), { color: PAL.teal, glow: true, ...sh });
  if (elite) {
    // tower shield: vault-door bars, rivet rows and a four-point teal seal
    for (const x of [-0.36, 0.36]) put(0.1, H - 0.1, 0.05, x, cy, 0.07, PAL.copperDark);
    for (const y of [-0.55, 0.75]) put(W - 0.12, 0.05, 0.04, 0, cy + y, 0.07, PAL.ironLight);
    for (const [dx, dy] of [[0, 0.28], [0, -0.08], [0.19, 0.1], [-0.19, 0.1]]) {
      b.add(at(plate(0.06, 0.06, 0.04, 0.005, 1), SHIELD_X + dx, cy + dy, z + 0.12, 0, yaw, Math.PI / 4), { color: PAL.teal, glow: true, ...sh });
    }
    put(W * 0.6, 0.16, 0.12, 0, cy + H / 2 + 0.02, -0.02, PAL.iron, false, 0.04); // crown lip
  }
  return b.build();
}

export const ART: EnemyArt = { name: 'Sealbound Bulwark', build, stride: 2.2, bob: 0.03, outline: 0.04 };
